import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, Loader2, Zap, Truck, ShieldCheck, Package, Star } from "lucide-react";
import { Reveal } from "./motion";
import { trackInitiateCheckout } from "@/lib/tracking";
import {
  BUNDLES,
  SHIP_FEE,
  IMAGES,
  SHOPIFY_DOMAIN,
  SHOPIFY_VARIANT_ID,
} from "@/lib/product";

const BENEFITS = [
  "Locked-in focus for 6+ hours — no jitters",
  "Zero afternoon crash, ever",
  "Lion's Mane · Cordyceps · Chaga, clinically dosed",
  "Tastes like a smooth dark roast, not dirt",
  "USDA Organic · Vegan · Gluten-free",
];

export const ProductSection = () => {
  const [bags, setBags] = useState(3);
  const [loading, setLoading] = useState(false);

  const bundle = BUNDLES.find((b) => b.bags === bags);
  const price = bundle.price;
  const compareAt = bundle.base;
  const freeShip = true; // launch offer: free shipping, no minimum
  const perServing = price / (bundle.bags * 30);

  const startCheckout = () => {
    if (SHOPIFY_DOMAIN.includes("your-store")) {
      toast.error("Store not connected yet — add your Shopify domain to go live.");
      return;
    }
    setLoading(true);
    // Forward UTM / click-ids onto the final checkout URL for attribution.
    const cur = new URLSearchParams(window.location.search);
    const utm = new URLSearchParams();
    ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "fbclid", "ttclid"].forEach(
      (k) => {
        const v = cur.get(k);
        if (v) utm.set(k, v);
      }
    );
    let checkout = "/checkout";
    const uq = utm.toString();
    if (uq) checkout += `?${uq}`;

    // Every order uses the single Supliful-connected variant; the bundle only
    // changes the quantity (1 / 3 / 6 bags) so orders route to Supliful. Clear
    // first so the cart holds exactly the selected bundle, then go to checkout.
    const add =
      `/cart/add?id=${SHOPIFY_VARIANT_ID}&quantity=${bundle.qty}` +
      `&return_to=${encodeURIComponent(checkout)}`;
    const url = `https://${SHOPIFY_DOMAIN}/cart/clear?return_to=${encodeURIComponent(add)}`;

    trackInitiateCheckout({ value: price, variantId: SHOPIFY_VARIANT_ID, quantity: bundle.qty });
    setTimeout(() => {
      window.location.href = url;
    }, 250);
    setTimeout(() => setLoading(false), 4000);
  };

  return (
    <section
      id="buy"
      data-testid="buy-section"
      className="border-y border-zinc-800 bg-[#09090B]"
    >
      <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-10 px-5 py-24 sm:px-8 sm:py-28 lg:grid-cols-12 lg:gap-16">
        {/* Left — gallery + benefits */}
        <div className="lg:col-span-7">
          <Reveal>
            <div className="relative overflow-hidden border border-zinc-800 bg-surface-100">
              <div className="absolute left-1/2 top-1/2 -z-0 h-64 w-64 -translate-x-1/2 -translate-y-1/2 rounded-full bg-blaze/15 blur-[90px]" />
              <img
                src={IMAGES.bagHero}
                alt="OVERCLOCK God Mode Mushroom Coffee"
                className="relative z-10 mx-auto max-h-[520px] w-auto object-contain p-6"
              />
              <div className="absolute left-4 top-4 z-20 flex items-center gap-1 border border-blaze bg-blaze px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-black">
                <Zap className="h-3 w-3" fill="currentColor" /> Best Seller
              </div>
            </div>
          </Reveal>

          <div className="mt-4 grid grid-cols-2 gap-4">
            <Reveal delay={0.05}>
              <div className="overflow-hidden border border-zinc-800 bg-surface-100">
                <img src={IMAGES.lifestyleGym} alt="Train harder with God Mode" className="h-40 w-full object-cover grayscale transition-all duration-700 hover:grayscale-0 sm:h-56" />
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="overflow-hidden border border-zinc-800 bg-surface-100">
                <img src={IMAGES.lifestyleCoffee} alt="God Mode morning ritual" className="h-40 w-full object-cover grayscale transition-all duration-700 hover:grayscale-0 sm:h-56" />
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <ul className="mt-8 grid gap-3">
              {BENEFITS.map((b) => (
                <li key={b} className="flex items-start gap-3 text-zinc-200">
                  <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center bg-blaze">
                    <Check className="h-3.5 w-3.5 text-black" strokeWidth={3} />
                  </span>
                  <span className="text-base">{b}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>

        {/* Right — sticky buy box */}
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-24">
            <div
              data-testid="buy-box"
              className="border border-zinc-800 bg-surface-100 p-6 sm:p-8"
            >
              <div className="flex items-center gap-2">
                <div className="flex">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-blaze text-blaze" />
                  ))}
                </div>
                <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-400">
                  4.9 · 2,900+ reviews
                </span>
              </div>

              <h2 className="mt-4 font-display text-4xl uppercase leading-none tracking-tight sm:text-5xl">
                God Mode
                <br />
                Mushroom Coffee
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-zinc-400">
                12oz whole-batch ground · 30 cups per bag. The daily upgrade for
                relentless output.
              </p>

              {/* Bundle selector */}
              <div className="mt-7">
                <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                  Choose your supply
                </p>
                <div className="grid gap-3">
                  {BUNDLES.map((b) => {
                    const active = b.bags === bags;
                    return (
                      <button
                        key={b.bags}
                        data-testid={`bag-selector-${b.bags}`}
                        onClick={() => setBags(b.bags)}
                        className={`relative flex items-center justify-between border px-4 py-4 text-left transition-colors duration-200 ${
                          active
                            ? "border-blaze bg-blaze/10"
                            : "border-zinc-800 bg-[#09090B] hover:border-zinc-600"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <span
                            className={`flex h-5 w-5 flex-none items-center justify-center rounded-full border ${
                              active ? "border-blaze bg-blaze" : "border-zinc-600"
                            }`}
                          >
                            {active && <Check className="h-3 w-3 text-black" strokeWidth={3} />}
                          </span>
                          <div>
                            <div className="font-display text-xl uppercase tracking-tight">
                              {b.label}
                            </div>
                            <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">
                              {b.servings}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-display text-xl text-white">
                            ${b.price.toFixed(2)}
                          </div>
                          {b.tag && (
                            <div className="font-mono text-[10px] font-bold uppercase tracking-[0.1em] text-blaze">
                              {b.tag}
                            </div>
                          )}
                        </div>
                        {b.popular && (
                          <span className="absolute -top-2.5 right-3 bg-blaze px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-[0.15em] text-black">
                            Most Popular
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Price summary */}
              <div className="mt-6 border-t border-zinc-800 pt-5">
                <div className="flex items-end justify-between">
                  <div>
                    <div className="flex items-baseline gap-2">
                      <span
                        data-testid="total-price"
                        className="font-display text-5xl leading-none text-white"
                      >
                        ${price.toFixed(2)}
                      </span>
                      {price < compareAt && (
                        <span className="font-mono text-sm text-zinc-500 line-through">
                          ${compareAt.toFixed(2)}
                        </span>
                      )}
                    </div>
                    <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">
                      ${perServing.toFixed(2)} / cup
                    </div>
                  </div>
                  <div
                    data-testid="shipping-note"
                    className={`flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.12em] ${
                      freeShip ? "text-emerald-400" : "text-zinc-400"
                    }`}
                  >
                    <Truck className="h-4 w-4" />
                    {freeShip ? "Free shipping" : `+ $${SHIP_FEE} shipping`}
                  </div>
                </div>
                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.1em] text-emerald-400">
                  Launch offer: free shipping on every order — no minimum
                </p>
              </div>

              {/* CTA */}
              <motion.button
                data-testid="add-to-cart-button"
                onClick={startCheckout}
                disabled={loading}
                whileTap={{ scale: 0.98 }}
                className="mt-6 flex w-full items-center justify-center gap-2 bg-blaze py-5 font-mono text-sm font-bold uppercase tracking-[0.15em] text-black transition-colors duration-200 hover:bg-blaze-hover disabled:opacity-70"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Redirecting…
                  </>
                ) : (
                  <>
                    <Zap className="h-4 w-4" fill="currentColor" /> Secure My Order
                  </>
                )}
              </motion.button>

              <div className="mt-5 grid grid-cols-2 gap-3 border-t border-zinc-800 pt-5">
                <Trust icon={Package} label="Ships Fast · USA" />
                <Trust icon={ShieldCheck} label="Secure Checkout" />
              </div>
              <p className="mt-3 text-center font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-600">
                Secure checkout · Powered by Shopify
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Trust = ({ icon: Icon, label }) => (
  <div className="flex items-center gap-2">
    <Icon className="h-4 w-4 text-emerald-400" />
    <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-zinc-400">
      {label}
    </span>
  </div>
);
